import React from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import SectionHeading from "../components/SectionHeading";
import {
  ArrowRight,
  ShieldCheck,
  FileText,
  Target,
  BadgeCheck,
  CheckCircle2,
  Phone,
  Award,
  TrendingUp,
  Sparkles,
  Briefcase,
  Layers,
  ClipboardCheck,
  CalendarCheck,
  PhoneCall,
  FileSignature,
  Users2,
  ChevronRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  trustStats,
  heroTrustBadges,
  registrationBenefits,
  registrationSteps,
  ndisOpportunity,
  authorityCards,
  faqs,
  images,
  pricingPackages,
  supportedOrgs,
} from "../mock";

const pkgIconMap = { FileText, Briefcase, Award, Layers };
const authorityIconMap = { ShieldCheck, Target, Layers, CalendarCheck };
const processIconMap = { PhoneCall, FileSignature, ShieldCheck, Users2 };

export default function Home() {
  return (
    <main>
      <Hero />

      {/* Social proof stats strip — premium */}
      <section className="pt-20 md:pt-24 reveal-on-scroll" data-testid="home-stats-strip">
        <div className="container-x">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {trustStats.map((s) => (
              <div
                key={s.title}
                className="relative bg-white rounded-2xl p-7 border border-slate-200 card-soft overflow-hidden"
                data-testid={`stat-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-teal-soft/50 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative">
                  <div className="text-4xl md:text-[44px] font-bold text-teal leading-none">
                    {s.number}
                  </div>
                  <div className="mt-4 w-10 h-px bg-teal" />
                  <h3 className="mt-4 text-navy font-bold text-[16px] leading-snug">{s.title}</h3>
                  <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Organisations We Have Supported — trust grid */}
      <section className="pt-16 md:pt-20 reveal-on-scroll" data-testid="home-organisations-supported">
        <div className="container-x">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              {/* LEFT — heading & intro */}
              <div className="lg:col-span-5 p-8 md:p-10 lg:border-r border-slate-200 bg-mist">
                <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-teal">
                  <BadgeCheck size={14} /> Trusted By
                </span>
                <h2 className="mt-4 text-navy font-bold text-2xl md:text-3xl leading-[1.15]">
                  Organisations We Have Supported
                </h2>
                <p className="mt-5 text-[15px] text-slate-600 leading-[1.7]">
                  These Australian organisations have received NDIS registration,
                  compliance and audit readiness support from Apex Quality Consultancy.
                </p>
                <Link
                  to="/contact"
                  className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-teal hover:text-navy transition"
                  data-testid="organisations-supported-cta"
                >
                  Join them <ArrowRight size={14} />
                </Link>
              </div>

              {/* RIGHT — organisation cards grid */}
              <div className="lg:col-span-7 p-6 md:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {supportedOrgs.map((org) => (
                    <div
                      key={org.name}
                      className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-4 hover:border-teal hover:bg-mist/50 transition-colors"
                      data-testid={`organisation-${org.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    >
                      <div className="w-11 h-11 rounded-xl bg-navy text-teal-light flex items-center justify-center font-bold text-[14px] shrink-0">
                        {org.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-navy font-bold text-[15px] leading-snug">
                          {org.name}
                        </p>
                        <p className="mt-1 text-[12px] text-slate-500 leading-snug">
                          NDIS registration, compliance and audit readiness support
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <p
                  className="mt-6 text-center text-teal font-semibold text-[15px]"
                  data-testid="organisations-many-more"
                >
                  Many more!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achieve NDIS Registration — benefits with image */}
      <section className="py-20 md:py-24 reveal-on-scroll" data-testid="home-achieve-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -top-5 -left-5 w-32 h-32 bg-teal-soft rounded-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                <img
                  src={images.consulting}
                  alt="NDIS consulting"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[440px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 right-2 sm:-right-6 bg-navy text-white rounded-2xl p-5 shadow-xl max-w-[240px] sm:max-w-[260px]">
                <BadgeCheck className="text-teal-mid" size={26} />
                <p className="mt-2 text-sm font-bold">Audit Informed Support</p>
                <p className="text-[12px] text-slate-300 mt-1 leading-relaxed">
                  Practical guidance from people who understand audit evidence.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <SectionHeading
              eyebrow="Become a Registered Provider"
              title="Achieve your NDIS registration or renewal with confidence."
              subtitle="We simplify NDIS registration and audit readiness so you can focus on delivering quality care."
            />

            <div className="mt-8 space-y-4" data-testid="home-benefits-list">
              {registrationBenefits.map((b) => (
                <div
                  key={b.title}
                  className="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200 card-soft"
                >
                  <div className="w-10 h-10 rounded-xl bg-teal-soft text-teal flex items-center justify-center shrink-0">
                    <CheckCircle2 size={20} />
                  </div>
                  <div>
                    <h4 className="text-navy font-bold text-[15px]">{b.title}</h4>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{b.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm"
                data-testid="achieve-book-cta"
              >
                Book Discovery Call <ArrowRight size={16} />
              </Link>
              <Link
                to="/pricing"
                className="btn-secondary inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Process — 4 steps */}
      <section className="py-20 md:py-24 bg-[#F4F5FA] reveal-on-scroll" data-testid="home-process-section">
        <div className="container-x">
          <SectionHeading
            align="center"
            eyebrow="Our Process"
            title="Our Process"
            subtitle="Practical support from registration preparation through to audit readiness and ongoing compliance guidance."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-12 lg:gap-y-0 lg:gap-x-4 relative">
            {registrationSteps.map((p, idx) => {
              const Icon = processIconMap[p.icon] || ShieldCheck;
              const stepNum = idx + 1;
              return (
                <div
                  key={p.step}
                  className="relative flex flex-col items-center text-center px-2"
                  data-testid={`process-step-${stepNum}`}
                >
                  {/* Numbered badge */}
                  <div className="relative">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-teal text-white flex items-center justify-center font-bold text-sm shadow-lg z-10">
                      {stepNum}
                    </div>
                    <div className="w-24 h-24 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-teal mt-3">
                      <Icon size={36} strokeWidth={1.6} />
                    </div>
                  </div>

                  {/* Chevron connector (between cards, not after last) */}
                  {idx < registrationSteps.length - 1 && (
                    <div className="hidden lg:flex absolute top-1/2 -translate-y-[55%] right-[-6px] w-12 items-center justify-center">
                      <ChevronRight size={26} className="text-teal/60" strokeWidth={1.6} />
                    </div>
                  )}

                  <h4 className="mt-6 text-navy font-bold text-[18px] leading-snug max-w-[200px]">
                    {p.title}
                  </h4>
                  <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed max-w-[230px]">
                    {p.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Packages — 3 package preview cards */}
      <section className="py-20 md:py-24 reveal-on-scroll" data-testid="home-packages-section">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <SectionHeading
              eyebrow="Our NDIS Packages"
              title="Choose the package that suits your stage"
              subtitle="From DIY templates to full audit readiness support — three premium packages designed for providers at every stage."
            />
            <Link
              to="/pricing"
              className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-teal hover:text-navy transition"
              data-testid="home-view-all-pricing"
            >
              Compare All Packages <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pricingPackages.map((pkg) => {
              const Icon = pkgIconMap[pkg.icon] || FileText;
              const popular = pkg.popular;
              return (
                <div
                  key={pkg.slug}
                  data-testid={`home-pkg-${pkg.slug}`}
                  className={`relative bg-white rounded-2xl border ${
                    popular
                      ? "border-teal shadow-xl lg:-translate-y-2"
                      : "border-slate-200 shadow-md"
                  } card-soft flex flex-col overflow-hidden`}
                >
                  {popular && (
                    <div className="absolute top-0 left-0 right-0 bg-teal text-white text-center text-[10.5px] font-bold uppercase tracking-[0.18em] py-2 flex items-center justify-center gap-1.5">
                      <Sparkles size={12} /> Most Popular
                    </div>
                  )}
                  <div className={`p-7 flex flex-col flex-1 ${popular ? "pt-11" : ""}`}>
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal">
                      Package {pkg.number}
                    </span>
                    <div className="mt-3 flex items-center gap-3">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          popular ? "bg-teal text-white" : "bg-teal-soft text-teal"
                        }`}
                      >
                        <Icon size={22} />
                      </div>
                      <h3 className="text-navy font-bold text-[18px] leading-tight">
                        {pkg.name}
                      </h3>
                    </div>
                    <div className="mt-4 text-xl font-bold text-navy">{pkg.startingPrice}</div>
                    <p className="mt-3 text-[14px] text-slate-600 leading-relaxed italic flex-1">
                      {pkg.description}
                    </p>
                    <Link
                      to="/pricing"
                      className={`mt-6 inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl font-semibold text-sm transition ${
                        popular ? "btn-primary" : "btn-secondary"
                      }`}
                      data-testid={`home-pkg-cta-${pkg.slug}`}
                    >
                      Learn More <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NDIS Business Opportunity */}
      <section
        className="py-20 md:py-24 bg-navy text-white relative overflow-hidden"
        data-testid="home-opportunity-section"
      >
        <div className="absolute inset-0 dotted-bg opacity-15" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-teal/15 to-transparent" />

        <div className="container-x relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-2 bg-white/10 border border-teal-mid/40 text-teal-light px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.18em]">
                <TrendingUp size={13} /> Industry Opportunity
              </span>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold leading-tight">
                {ndisOpportunity.heading}
              </h2>
              <p className="mt-4 text-lg text-slate-200/95 leading-relaxed">
                {ndisOpportunity.subheading}
              </p>
              <p className="mt-4 text-[15px] text-slate-300 leading-relaxed">
                {ndisOpportunity.body}
              </p>
              <p className="mt-4 text-[12px] text-slate-400 italic">
                {ndisOpportunity.reference}
              </p>
              <Link
                to="/contact"
                className="mt-7 btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm"
                data-testid="opportunity-cta"
              >
                Start Your Registration <ArrowRight size={16} />
              </Link>
            </div>

            <div className="lg:col-span-6 grid grid-cols-2 gap-4">
              {ndisOpportunity.stats.map((s) => (
                <div
                  key={s.label}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-teal-mid/40 transition"
                  data-testid={`opportunity-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="text-3xl md:text-4xl font-bold text-teal-light">{s.value}</div>
                  <p className="mt-2 text-[13px] text-slate-300 leading-snug">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Built Around Real Audit Experience — split layout */}
      <section className="py-20 md:py-24 bg-white reveal-on-scroll" data-testid="home-authority-section">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* LEFT — heading + intro */}
            <div className="lg:col-span-4">
              <h2 className="text-navy font-bold text-3xl md:text-4xl lg:text-[40px] leading-[1.1]">
                Built Around
                <br />
                <span className="text-teal">Real Audit Experience</span>
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed text-[15px]">
                Apex Quality Consulting provides practical NDIS registration, compliance and audit
                readiness support informed by senior experienced accredited auditors with real
                understanding of provider implementation challenges, evidence expectations and
                audit processes.
              </p>
            </div>

            {/* RIGHT — 4 cards in a 2×2 grid */}
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="authority-cards">
              {authorityCards.map((a) => {
                const Icon = authorityIconMap[a.icon] || ShieldCheck;
                return (
                  <div
                    key={a.title}
                    data-testid={`authority-card-${a.title
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/^-|-$/g, "")}`}
                    className="bg-white rounded-2xl p-7 border border-slate-200 shadow-md hover:shadow-lg transition-shadow"
                  >
                    <div className="w-12 h-12 rounded-xl bg-teal-soft text-teal flex items-center justify-center mb-5">
                      <Icon size={24} />
                    </div>
                    <h4 className="text-navy font-bold text-[17px] leading-snug">{a.title}</h4>
                    <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">{a.text}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-14 p-5 bg-teal-soft rounded-xl border-l-4 border-teal max-w-3xl mx-auto">
            <p className="text-sm text-slate-700 leading-relaxed text-center">
              <strong className="text-navy">Important.</strong> We provide consultancy and
              compliance preparation services. We do not act as the independent external Approved
              Quality Auditor for providers we have consulted to.
            </p>
          </div>
        </div>
      </section>

      {/* Quality Commitment band */}
      <section className="py-20 md:py-24 reveal-on-scroll" data-testid="home-quality-band">
        <div className="container-x">
          <div className="bg-teal-soft border border-teal/30 rounded-3xl p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-2 flex lg:justify-center">
              <div className="w-20 h-20 rounded-2xl bg-teal flex items-center justify-center">
                <BadgeCheck size={36} className="text-white" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-teal">
                Documentation Quality Commitment
              </span>
              <h3 className="mt-2 text-2xl md:text-3xl font-bold text-navy leading-tight">
                We stand behind the quality of our documentation.
              </h3>
              <p className="mt-3 text-slate-700 leading-relaxed">
                If any major compliance issue arises directly from documentation, policies, forms or
                templates supplied by Apex, we will work with you to correct and resolve it at no
                additional cost.
              </p>
            </div>
            <div className="lg:col-span-3 flex lg:justify-end">
              <Link
                to="/quality-commitment"
                className="btn-primary px-5 py-3 rounded-lg font-semibold text-sm inline-flex items-center gap-2"
              >
                Read Full Commitment <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24 bg-mist reveal-on-scroll" data-testid="home-faq-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="FAQ"
              title="Frequently asked questions"
              subtitle="Honest, compliance focused answers to the questions providers ask most."
            />
            <div className="mt-8 p-5 bg-white rounded-xl border border-slate-200 card-soft">
              <div className="flex items-center gap-3">
                <Phone size={20} className="text-teal" />
                <p className="text-sm font-semibold text-navy">Still have questions?</p>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Book a free 15 minute discovery call to discuss your registration pathway.
              </p>
              <Link
                to="/contact"
                className="mt-4 btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold"
              >
                Book Discovery Call <ArrowRight size={15} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((f, idx) => (
                <AccordionItem
                  key={idx}
                  value={`item-${idx}`}
                  className="bg-white rounded-xl border border-slate-200 px-5 data-[state=open]:border-teal data-[state=open]:shadow-lg transition"
                >
                  <AccordionTrigger className="text-left text-navy font-semibold text-[15px] hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-[15px] leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-24">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl p-10 md:p-14 bg-navy">
            <div className="absolute inset-0 dotted-bg opacity-25" />
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-teal/20 to-transparent" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <h2 className="text-white text-3xl md:text-4xl font-bold tracking-tight leading-tight">
                  Ready to start your audit readiness journey?
                </h2>
                <p className="mt-4 text-slate-300 text-lg max-w-2xl">
                  Book a free 15 minute discovery call — no obligation, no pressure.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link
                  to="/contact"
                  className="btn-primary px-6 py-3.5 rounded-lg font-semibold inline-flex items-center gap-2"
                  data-testid="home-final-cta"
                >
                  Book Discovery Call <ArrowRight size={17} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
